import { useQueryClient } from '@tanstack/react-query'
import { type ReactNode, useReducer } from 'react'

import { captureException } from '@/domains/observability'
import { useCooldown } from '@/lib/use-cooldown'

import { authClient } from './auth-client'
import {
  type EmailChangeAction,
  type EmailChangeState,
  EmailChangeContext
} from './email-change-context'
import { useUser } from './use-user'

const RESEND_COOLDOWN_SEC = 30

const initialState: EmailChangeState = {
  step: 'email',
  newEmail: '',
  code: '',
  error: '',
  submitting: false
}

function reducer(
  state: EmailChangeState,
  action: EmailChangeAction
): EmailChangeState {
  switch (action.type) {
    case 'SET_NEW_EMAIL':
      return { ...state, newEmail: action.payload, error: '' }
    case 'SET_CODE':
      return { ...state, code: action.payload, error: '' }
    case 'SUBMIT_START':
      return { ...state, submitting: true, error: '' }
    case 'SET_ERROR':
      return { ...state, submitting: false, error: action.payload }
    case 'CODE_SENT':
      return { ...state, step: action.step, code: '', submitting: false }
    case 'CHANGED':
      return { ...state, step: 'done', submitting: false }
    case 'RESET':
      return initialState
    default:
      return state
  }
}

type AuthResult = { error: { message?: string } | null }

/** better auth's two-code email change: a code to the current address, then
 *  one to the new address. */
export function EmailChangeProvider({
  children,
  onChanged
}: {
  children: ReactNode
  onChanged?: () => void
}) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const { user, refetch } = useUser()
  const queryClient = useQueryClient()
  const cooldown = useCooldown(RESEND_COOLDOWN_SEC)
  const currentEmail = user?.email ?? ''
  const newEmail = state.newEmail.trim()

  /* better auth reports a bad code as { error }; a throw is a network failure. */
  async function run(
    call: () => Promise<AuthResult>,
    fallback: string
  ): Promise<boolean> {
    dispatch({ type: 'SUBMIT_START' })
    try {
      const { error } = await call()
      if (!error) return true
      dispatch({ type: 'SET_ERROR', payload: error.message ?? fallback })
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)))
      dispatch({
        type: 'SET_ERROR',
        payload: 'Something went wrong. Please try again.'
      })
    }
    return false
  }

  const sendCurrentCode = () =>
    authClient.emailOtp.sendVerificationOtp({
      email: currentEmail,
      type: 'email-verification'
    })

  async function start() {
    if (newEmail.toLowerCase() === currentEmail.toLowerCase()) {
      dispatch({ type: 'SET_ERROR', payload: 'That is already your email.' })
      return
    }
    if (await run(sendCurrentCode, 'Could not send the code. Try again.')) {
      dispatch({ type: 'CODE_SENT', step: 'current-code' })
    }
  }

  async function confirmCode(otp: string) {
    if (state.step === 'current-code') {
      const ok = await run(
        () => authClient.emailOtp.requestEmailChange({ newEmail, otp }),
        'That code is invalid or expired. Resend it.'
      )
      if (ok) dispatch({ type: 'CODE_SENT', step: 'new-code' })
      return
    }

    /* an address already registered here gets no code at all (better auth
     * withholds it rather than confirm the account exists), so name both causes. */
    const ok = await run(
      () => authClient.emailOtp.changeEmail({ newEmail, otp }),
      'That code is invalid or expired. If no code arrived, that address may already be in use on TandemOne.'
    )
    if (!ok) return
    await refetch()
    /* setup derives a buyer's work-email step from this address. */
    void queryClient.invalidateQueries({ queryKey: ['setup'] })
    dispatch({ type: 'CHANGED' })
    onChanged?.()
  }

  async function resend() {
    const step = state.step === 'new-code' ? 'new-code' : 'current-code'
    const ok = await run(
      step === 'current-code'
        ? sendCurrentCode
        : () =>
            authClient.emailOtp.requestEmailChange({
              newEmail,
              otp: state.code
            }),
      'Could not resend the code. Start over.'
    )
    if (!ok) return
    dispatch({ type: 'CODE_SENT', step })
    cooldown.start()
  }

  function reset() {
    dispatch({ type: 'RESET' })
    cooldown.clear()
  }

  return (
    <EmailChangeContext.Provider
      value={{
        state,
        dispatch,
        currentEmail,
        codeAddress: state.step === 'new-code' ? newEmail : currentEmail,
        resendCooldown: cooldown.remaining,
        start,
        confirmCode,
        resend,
        reset
      }}
    >
      {children}
    </EmailChangeContext.Provider>
  )
}
